import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [ready, setReady] = useState(false);
    const [invitesCount, setInvitesCount] = useState(0);
    const pollRef = useRef(null);

    const refreshInvites = useCallback(async () => {
        try {
            const r = await api.get("/my/invites/count");
            setInvitesCount(r.data?.count || 0);
        } catch { setInvitesCount(0); }
    }, []);

    useEffect(() => {
        const token = localStorage.getItem("rp_token");
        // Always call /auth/me — session cookie may auth us even without a JWT token
        api.get("/auth/me")
            .then(r => setUser(r.data))
            .catch(() => { if (token) localStorage.removeItem("rp_token"); })
            .finally(() => setReady(true));
    }, []);

    // Poll invite count while signed in
    useEffect(() => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        if (!user) { setInvitesCount(0); return; }
        refreshInvites();
        pollRef.current = setInterval(refreshInvites, 20000);
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, [user, refreshInvites]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        localStorage.setItem("rp_token", data.token);
        setUser(data.user);
        return data.user;
    };
    const register = async (payload) => {
        const { data } = await api.post("/auth/register", payload);
        localStorage.setItem("rp_token", data.token);
        setUser(data.user);
        return data.user;
    };
    const logout = async () => {
        try { await api.post("/auth/logout"); } catch {}
        localStorage.removeItem("rp_token");
        setUser(null);
    };
    const refreshUser = useCallback(async () => {
        try { const r = await api.get("/auth/me"); setUser(r.data); } catch {}
    }, []);

    return (
        <AuthContext.Provider value={{ user, ready, login, register, logout, invitesCount, refreshInvites, setUser, refreshUser }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
